import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

const AUTH_TOKEN_KEY = 'auth_token';

const baseURL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

const timeout = Number(import.meta.env.VITE_API_TIMEOUT ?? 15000);

export interface ApiErrorPayload {
  code?: string;
  message?: string;
  details?: unknown;
}

export const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setAuthToken = (token: string): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

export const clearAuthToken = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

// ---------- 请求拦截器：自动附带 Bearer Token ----------
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------- 401 并发去重 ----------
let last401Time = 0;
const DEDUP_WINDOW_MS = 2000; // 2 秒内的重复 401 只触发一次事件

// ---------- 响应拦截器：统一错误码处理 ----------
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status;
    const payload = error.response?.data;
    const message = payload?.message ?? error.message ?? 'Unknown error';

    switch (status) {
      case 401:
        clearAuthToken();
        // 并发去重：同一窗口期内的多次 401 仅广播一次事件
        if (Date.now() - last401Time > DEDUP_WINDOW_MS) {
          last401Time = Date.now();
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        console.warn('[apiClient] 401 未登录或 Token 失效');
        break;
      case 403:
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
        console.warn('[apiClient] 403 无权限访问');
        break;
      case 404:
        console.warn(`[apiClient] 404 资源不存在: ${error.config?.url}`);
        break;
      case 422:
        console.warn(`[apiClient] 422 参数校验失败: ${message}`);
        break;
      case 429:
        console.warn('[apiClient] 429 请求过于频繁');
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        window.dispatchEvent(
          new CustomEvent('api:server-error', { detail: { status, message } }),
        );
        console.error(`[apiClient] ${status} 服务器异常: ${message}`);
        break;
      default:
        if (!error.response) {
          console.error('[apiClient] 网络错误或请求被中断', error.message);
        }
    }

    return Promise.reject(error);
  },
);

// ---------- 便捷的泛型 HTTP 方法封装 ----------
export const http = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((r) => r.data),

  post: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((r) => r.data),

  put: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((r) => r.data),

  patch: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((r) => r.data),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((r) => r.data),
};

export default apiClient;
